import type { GridColDef } from '@mui/x-data-grid';
import type { Theme, SxProps } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import useSWR from 'swr';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { useState } from 'react';
import { useParams } from 'react-router';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { paperClasses } from '@mui/material/Paper';

import axiosInstance, { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import { SX_GRADE_CANVAS } from 'src/auditoria/components/ds';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { AnexosInput } from 'src/components/anexos-input';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import { arquivosParaAnexos } from 'src/utils/ler-base64';
import { anexarProtocolo, baixarProtocoloDoc } from 'src/actions/anexos';

import { formatDateBr } from '../Nfse/types';

// ----------------------------------------------------------------------
// Protocolo → detalhe do processo (duplo-clique em Meus Processos). Backend:
// GET /portal-api/protocolo/detalhe?protocolo=<cp_protocolo>. Abas: Dados,
// Mensagens, Tramitação, Documentos — todas do schema fiscal `protocolo`
// (processo_mensagem / processo_tramitacao / processo_documento por cp_protocolo).
// O contribuinte só vê o PRÓPRIO processo (guard fkpersona no backend).
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Casulo do canvas SEM o respiro lateral.
//
// As duas grades desta tela moram no `<Box sx={{ p: 3 }}>` que é o corpo do `<Card>` das
// abas — ele já paga 24px de cada lado; com o
// recuo de 16px do kit as colunas ficariam 40px para dentro e sairiam do prumo do
// título do cartão. Por isso o `paddingInline` do `.MuiDataGrid-main` é zerado — e SÓ
// ele: o `::before` (faixa de fundo do cabeçalho) e o `::after` (as duas réguas do
// `.thead`) continuam, porque o kit desliga as bordas nativas em
// `.MuiDataGrid-columnHeaders` e sem os pseudo-elementos o cabeçalho ficaria sem régua
// nenhuma. As barras de rolagem voltam para a borda: são `position:absolute` na caixa de
// padding do `main` e ficariam 16px soltas.
//
// O `sx` do kit chega tipado com o `Theme` do app e o `TxDataGrid` do portal redeclara a
// prop como `SxProps` — a conversão é só de tipo, o objeto é o mesmo (mesma nota do
// `SubUsuarios/List`).
// ----------------------------------------------------------------------

const { sx: SX_GRADE, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_CANVAS = SX_GRADE as Record<string, SystemStyleObject<Theme>>;

const SX_GRADE_SEM_RESPIRO = {
  ...SX_CANVAS,
  '& .MuiDataGrid-main': { ...SX_CANVAS['& .MuiDataGrid-main'], paddingInline: 0 },
  '& .MuiDataGrid-scrollbar--vertical': { right: 0 },
  '& .MuiDataGrid-scrollbar--horizontal': { left: 0, width: '100%' },
} as SxProps;
/**
 * O `TxDataGrid` se embrulha num `<Paper variant="outlined">` próprio
 * (`TxDataGridClient.tsx:136`). Dentro do cartão da tela isso desenha uma segunda
 * superfície sobre a primeira — um cartão dentro do cartão — e, como a grade está sem o
 * respiro de 16px, as colunas encostam naquela borda interna em vez de ficarem no prumo
 * do título. Achatado, o cartão da tela volta a ser a única superfície e o eixo é um só.
 * Mesmo bloco de `conta-fiscal/views/index-view.tsx:133` e do `GradeCanvas`.
 */
const SX_ACHATA_PAPER_DA_GRADE = {
  [`& > .${paperClasses.root}`]: {
    border: 0,
    borderRadius: 0,
    boxShadow: 'none',
    backgroundImage: 'none',
  },
} as const;

interface Mensagem {
  id: number;
  mensagem: string | null;
  data_hora: string | null;
  remetente: 'A' | 'C' | null;
  visualizou_contribuinte: boolean | null;
  visualizou_fiscal: boolean | null;
}
interface Tramite {
  id: number;
  data: string | null;
  prazo: string | null;
  unidade: string | null;
  status: string | null;
  descricao: string | null;
}
interface Documento {
  id: number;
  nome: string | null;
  extensao: string | null;
  tipo: string | null;
  aprovacao: string | null;
}
interface ProcessoDetalhe {
  processo: {
    protocolo: string;
    tipo: string | null;
    assunto: string | null;
    status: string | null;
    situacao_id: number | null;
    aguardando_contribuinte: boolean | null;
    aguardando_fiscal: boolean | null;
    unidade: string | null;
    data: string | null;
    hora: string | null;
    prazo: string | null;
    descricao: string | null;
    solicitante: string | null;
    documento: string | null;
    email: string | null;
    telefone: string | null;
  };
  mensagens: Mensagem[];
  tramitacao: Tramite[];
  documentos: Documento[];
}

function formatDateTimeBr(value: string | null | undefined): string {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '—';
}

function CampoInfo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </Typography>
    </Grid>
  );
}

export default function ProtocoloDetailPage() {
  const { numero = '' } = useParams();
  const protocolo = decodeURIComponent(numero);
  const [tab, setTab] = useState('dados');

  const { data, isLoading, mutate } = useSWR<ProcessoDetalhe>(
    protocolo ? `/portal-api/protocolo/detalhe?protocolo=${encodeURIComponent(protocolo)}` : null,
    fetcher
  );
  const p = data?.processo;
  const mensagens = data?.mensagens ?? [];
  const tramitacao = data?.tramitacao ?? [];
  const documentos = data?.documentos ?? [];

  const [anexos, setAnexos] = useState<File[]>([]);
  const [enviandoAnexos, setEnviandoAnexos] = useState(false);

  // Chat + resposta formal à solicitação de informação
  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviandoMensagem, setEnviandoMensagem] = useState(false);
  const [respostaAnexos, setRespostaAnexos] = useState<File[]>([]);

  const aguardandoContribuinte = Boolean(p?.aguardando_contribuinte); // situação 3 — resposta formal
  const processoEncerrado = p?.situacao_id === 5 || p?.situacao_id === 6;

  // Bate-papo alternado: a vez é de quem NÃO mandou a última mensagem. A
  // situação (3/4) só governa o ciclo FORMAL de solicitação — usá-la para
  // travar o chat prendia o contribuinte em "aguarde" mesmo depois de o
  // fiscal responder (mensagem comum não muda a situação).
  const ultimaMensagem = mensagens.length ? mensagens[mensagens.length - 1] : null;
  const minhaVez = !ultimaMensagem || ultimaMensagem.remetente === 'A';

  const enviarMensagem = async () => {
    const texto = novaMensagem.trim();
    if (!texto) return;
    setEnviandoMensagem(true);
    try {
      await axiosInstance.post('/portal-api/protocolo/mensagens', { protocolo, mensagem: texto });
      setNovaMensagem('');
      await mutate();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Não foi possível enviar a mensagem.');
    } finally {
      setEnviandoMensagem(false);
    }
  };

  const responderSolicitacao = async () => {
    const texto = novaMensagem.trim();
    if (!texto) {
      toast.error('Escreva a resposta à solicitação.');
      return;
    }
    setEnviandoMensagem(true);
    try {
      const anexosPayload = await arquivosParaAnexos(respostaAnexos);
      await axiosInstance.post('/portal-api/protocolo/responder-solicitacao', {
        protocolo,
        mensagem: texto,
        anexos: anexosPayload,
      });
      toast.success('Resposta enviada ao fisco.');
      setNovaMensagem('');
      setRespostaAnexos([]);
      await mutate();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Não foi possível enviar a resposta.');
    } finally {
      setEnviandoMensagem(false);
    }
  };

  const enviarAnexos = async () => {
    if (!anexos.length) return;
    setEnviandoAnexos(true);
    try {
      const falhas = await anexarProtocolo(protocolo, anexos);
      if (falhas > 0) toast.error(`${falhas} anexo(s) não puderam ser enviados.`);
      else toast.success('Anexo(s) enviado(s).');
      setAnexos([]);
      await mutate();
    } finally {
      setEnviandoAnexos(false);
    }
  };

  const tramColumns: GridColDef<Tramite>[] = [
    { field: 'data', headerName: 'Data', flex: 0.7, minWidth: 110, valueGetter: (_v, r) => formatDateBr(r.data) },
    { field: 'unidade', headerName: 'Unidade', flex: 1.2, minWidth: 160, valueGetter: (_v, r) => r.unidade ?? '—' },
    { field: 'status', headerName: 'Situação', flex: 0.9, minWidth: 130, valueGetter: (_v, r) => r.status ?? '—' },
    { field: 'prazo', headerName: 'Prazo', flex: 0.7, minWidth: 110, valueGetter: (_v, r) => formatDateBr(r.prazo) },
    { field: 'descricao', headerName: 'Descrição', flex: 1.6, minWidth: 220, valueGetter: (_v, r) => r.descricao ?? '—' },
  ];

  const docColumns: GridColDef<Documento>[] = [
    { field: 'nome', headerName: 'Documento', flex: 1.4, minWidth: 200, valueGetter: (_v, r) => r.nome ?? '—' },
    { field: 'tipo', headerName: 'Tipo', flex: 1, minWidth: 150, valueGetter: (_v, r) => r.tipo ?? '—' },
    { field: 'extensao', headerName: 'Formato', flex: 0.5, minWidth: 90, valueGetter: (_v, r) => r.extensao ?? '—' },
    {
      field: 'aprovacao',
      headerName: 'Aprovação',
      flex: 0.7,
      minWidth: 120,
      valueGetter: (_v, r) => r.aprovacao ?? '—',
    },
    {
      field: 'acoes',
      headerName: '',
      width: 64,
      sortable: false,
      filterable: false,
      align: 'center',
      renderCell: (params) => (
        <Tooltip title="Baixar">
          <IconButton
            size="small"
            onClick={() => baixarProtocoloDoc(protocolo, params.row.id, params.row.nome ?? `documento-${params.row.id}`)}
          >
            <Iconify icon="solar:download-bold" width={18} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={p ? `Processo ${p.protocolo}` : 'Processo'}
        links={[
          { name: 'Protocolo' },
          { name: 'Meus Processos', href: '/protocolo' },
          { name: 'Detalhe' },
        ]}
        sx={{ mb: { xs: 2, md: 3 } }}
      />

      {!p ? (
        <Card sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {isLoading ? 'Carregando...' : 'Processo não encontrado.'}
          </Typography>
        </Card>
      ) : (
        <Card>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ px: 2 }}>
            <Tab value="dados" label="Dados" />
            <Tab value="mensagens" label={`Mensagens (${mensagens.length})`} />
            <Tab value="tramitacao" label={`Tramitação (${tramitacao.length})`} />
            <Tab value="documentos" label={`Documentos (${documentos.length})`} />
          </Tabs>
          <Divider />

          <Box sx={{ p: 3, ...SX_ACHATA_PAPER_DA_GRADE }}>
            {tab === 'dados' && (
              <Grid container spacing={2.5}>
                <CampoInfo label="Protocolo" value={p.protocolo} />
                <CampoInfo label="Tipo" value={p.tipo} />
                <CampoInfo label="Assunto" value={p.assunto} />
                <CampoInfo
                  label="Situação"
                  value={p.status ? <Label color="default">{p.status}</Label> : '—'}
                />
                <CampoInfo label="Unidade" value={p.unidade} />
                <CampoInfo label="Abertura" value={formatDateBr(p.data)} />
                <CampoInfo label="Prazo de resposta" value={formatDateBr(p.prazo)} />
                <CampoInfo label="Solicitante" value={p.solicitante} />
                <CampoInfo label="CPF/CNPJ" value={p.documento} />
                <CampoInfo label="E-mail" value={p.email} />
                <CampoInfo label="Telefone" value={p.telefone} />
                {p.descricao && (
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Descrição
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {p.descricao}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}

            {tab === 'mensagens' && (
              <Stack spacing={2}>
                {aguardandoContribuinte && (
                  <Alert severity="warning" icon={<Iconify icon="solar:chat-round-dots-bold" />}>
                    O fisco solicitou informações neste processo — responda no formulário abaixo.
                    Você pode anexar arquivos à resposta.
                  </Alert>
                )}
                {!aguardandoContribuinte && !processoEncerrado && !minhaVez && (
                  <Alert severity="info">
                    Mensagem enviada — aguardando resposta do fiscal.
                  </Alert>
                )}

                {mensagens.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Sem mensagens neste processo.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {mensagens.map((m) => {
                      const minha = m.remetente === 'C';
                      return (
                        <Box
                          key={m.id}
                          sx={{ display: 'flex', justifyContent: minha ? 'flex-end' : 'flex-start' }}
                        >
                          <Card
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              maxWidth: '80%',
                              bgcolor: minha ? 'primary.lighter' : 'background.neutral',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                            >
                              {minha ? 'Você' : 'Fisco'} · {formatDateTimeBr(m.data_hora)}
                              {minha && (m.visualizou_fiscal ? ' · lida pelo fisco' : ' · não lida')}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                              {m.mensagem}
                            </Typography>
                          </Card>
                        </Box>
                      );
                    })}
                  </Stack>
                )}

                {!processoEncerrado && (
                  <>
                    <Divider />
                    {aguardandoContribuinte ? (
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2">Responder à solicitação de informação</Typography>
                        <TextField
                          multiline
                          minRows={3}
                          fullWidth
                          placeholder="Escreva a resposta formal à solicitação do fisco…"
                          value={novaMensagem}
                          onChange={(e) => setNovaMensagem(e.target.value)}
                          disabled={enviandoMensagem}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <AnexosInput
                            arquivos={respostaAnexos}
                            onChange={setRespostaAnexos}
                            disabled={enviandoMensagem}
                          />
                          <Button
                            variant="contained"
                            onClick={responderSolicitacao}
                            disabled={enviandoMensagem || !novaMensagem.trim()}
                            startIcon={<Iconify icon="solar:letter-bold" />}
                          >
                            Responder solicitação
                          </Button>
                        </Box>
                      </Stack>
                    ) : !minhaVez ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Aguarde a resposta do fiscal para enviar uma nova mensagem.
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <TextField
                          multiline
                          minRows={2}
                          fullWidth
                          placeholder="Escreva uma mensagem ao fiscal…"
                          value={novaMensagem}
                          onChange={(e) => setNovaMensagem(e.target.value)}
                          disabled={enviandoMensagem}
                        />
                        <Button
                          variant="contained"
                          onClick={enviarMensagem}
                          disabled={enviandoMensagem || !novaMensagem.trim()}
                          sx={{ mt: 0.5, flexShrink: 0 }}
                          startIcon={<Iconify icon="solar:letter-bold" />}
                        >
                          Enviar
                        </Button>
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            )}

            {tab === 'tramitacao' && (
              <TxDataGrid<Tramite>
                rows={tramitacao}
                columns={tramColumns}
                getRowId={(r) => r.id}
                initialPageSize={10}
                {...PROPS_GRADE}
                sx={SX_GRADE_SEM_RESPIRO}
              />
            )}

            {tab === 'documentos' && (
              // A grade de Documentos não é filha direta do corpo do cartão: o `Stack`
              // que segura o input de anexos fica no meio. O mesmo bloco é reaplicado
              // aqui, onde o `Paper` do `TxDataGrid` é de fato o filho direto.
              <Stack spacing={2} sx={SX_ACHATA_PAPER_DA_GRADE}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <AnexosInput arquivos={anexos} onChange={setAnexos} disabled={enviandoAnexos} />
                  {anexos.length > 0 && (
                    <Button variant="contained" onClick={enviarAnexos} disabled={enviandoAnexos}>
                      Enviar {anexos.length} anexo(s)
                    </Button>
                  )}
                </Box>
                <TxDataGrid<Documento>
                  rows={documentos}
                  columns={docColumns}
                  getRowId={(r) => r.id}
                  initialPageSize={10}
                  {...PROPS_GRADE}
                  sx={SX_GRADE_SEM_RESPIRO}
                />
              </Stack>
            )}
          </Box>
        </Card>
      )}
    </DashboardContent>
  );
}
